import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Loader2 } from 'lucide-react';
import { userService } from '../../_services';
import { useMobileDetection } from '../../hooks/useMobileDetection';
import { MobileRestrictionMessage } from '@/components/ui/MobileRestrictionMessage';

function CreateGroup({ 
    titleText,
    whName,
    wholesalerId,
    activeWhPagin,
    activePagin,
    whRule,
    whSearch,
    closeCreateGroupPopup,
    openManageGroupPopup
}) {
    const [loading, setLoading] = useState(true);
    const [btnLoader, setBtnLoader] = useState(false);
    const [prefixData, setPrefixData] = useState([]);
    const [selectedPrefix, setSelectedPrefix] = useState('');
    const [otherPrependPrefix, setOtherPrependPrefix] = useState('');
    const [availableGroup, setAvailableGroup] = useState('N/A');
    const [numberOfGroups, setNumberOfGroups] = useState('1');
    const [prefixError, setPrefixError] = useState('');
    const [availableGroupError, setAvailableGroupError] = useState('');
    const [error, setError] = useState('');
    const [successMessage, setSuccessMessage] = useState('');
    const [useOther, setUseOther] = useState(false);
    const isMobile = useMobileDetection();

    const userdata = useMemo(() => {
        try {
            return JSON.parse(localStorage.getItem('user'));
        } catch {
            return null;
        }
    }, []);

    const handleResponse = (response) => {
        return response.text().then(text => {
            try {
                const data = text && JSON.parse(text);
                return data;
            } catch (error) {
                return [];
            }
        });
    };

    const availablePrefix = useCallback((currentPrefix) => {
        try {
            let matchedAvail = '';
            if (prefixData && prefixData.length > 0 && prefixData[0].gnlist && prefixData[0].gnlist.length > 0) {
                matchedAvail = prefixData[0].gnlist.filter((option) => {
                    return option.prefix !== "" && option.prefix.toUpperCase() === currentPrefix.toUpperCase();
                });
            }
            
            if (matchedAvail === '' || matchedAvail.length === 0) {
                setAvailableGroup(currentPrefix + '100');
            } else {
                setAvailableGroup(matchedAvail[0].gn);
            }
            setPrefixError('');
            setLoading(false);
        } catch (error) {

        }
    }, [prefixData]);

    const fetchDataLocally = useCallback(async (roleName, roleType, id) => {
        try {
            const requestOptions = {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ roleName, roleType, id })
            };
            
            const response = await fetch(`/api/admin/GroupPrefix`, requestOptions);
            const apidata = await handleResponse(response);
            
            if (apidata && apidata.length > 0) {
                setPrefixData(apidata);
                
                // Check if prefix array exists and has items
                if (apidata[0].prefix && apidata[0].prefix.length > 0) {
                    const firstPrefix = apidata[0].prefix[0];
                    setSelectedPrefix(firstPrefix);
                    
                    // Calculate available prefix for first option
                    if (apidata[0].gnlist && apidata[0].gnlist.length > 0) {
                        const matchedAvail = apidata[0].gnlist.filter((option) => {
                            return option.prefix !== "" && option.prefix.toUpperCase() === firstPrefix.toUpperCase();
                        });
                        
                        if (matchedAvail.length === 0) {
                            setAvailableGroup(firstPrefix + '100');
                        } else {
                            setAvailableGroup(matchedAvail[0].gn);
                        }
                    } else {
                        // No gnlist, default to prefix + 100
                        setAvailableGroup(firstPrefix + '100');
                    }
                }
            }
            setLoading(false);
        } catch (error) {

            setLoading(false);
        }
    }, []);

    useEffect(() => {
        if (userdata) {
            fetchDataLocally(userdata.paramsname, userdata.userrole, wholesalerId);
        }
    }, [userdata, wholesalerId, fetchDataLocally]);

    const handlePrefixChange = (value) => {
        if (value === 'Other') {
            setUseOther(true);
            setSelectedPrefix('');
            setAvailableGroup('N/A');
        } else {
            setUseOther(false);
            setSelectedPrefix(value);
            availablePrefix(value);
        }
    };

    const handleOtherPrefixChange = (e) => {
        const value = e.target.value.toUpperCase();
        setOtherPrependPrefix(value);
        
        if (value.length === 0 || value.length === 1) {
            setAvailableGroup('N/A');
        } else if (value.length > 6) {
            setAvailableGroup('N/A');
        } else {
            availablePrefix(value);
        }
    };

    const handleValidate = async (e) => {
        e.preventDefault();
        setBtnLoader(true);
        setPrefixError('');
        setAvailableGroupError('');
        setError('');

        const currentPrefix = useOther ? otherPrependPrefix : selectedPrefix;
        const midGroupValue = numberOfGroups;

        let isError = false;

        // Validation
        if (!currentPrefix || currentPrefix === '') {
            setPrefixError('Prefix required');
            isError = true;
        } else if (currentPrefix.length < 2 || currentPrefix.length > 6) {
            setPrefixError('Prefix should be minimum 2 characters and maximum 6 characters');
            isError = true;
        }

        if (!midGroupValue || midGroupValue === '' || midGroupValue === '0') {
            setAvailableGroupError('Number of Group Numbers cannot be blank or zero');
            isError = true;
        } else if (parseInt(midGroupValue) > 1000) {
            setAvailableGroupError('Number of Group Numbers should not be more than 1000');
            isError = true;
        }

        if (isError) {
            setBtnLoader(false);
            return;
        }

        // Create groups
        try {
            const requestOptions = {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    preFix: currentPrefix,
                    whName: whName,
                    whId: wholesalerId,
                    Count: midGroupValue
                })
            };

            const response = await fetch(`/api/admin/AddGroupNumberForWh`, requestOptions);
            const apidata = await handleResponse(response);

            if (apidata && apidata.length > 0) {
                if (apidata[0].code === "1") {
                    setSuccessMessage(apidata[0].statusMessage);
                    setError('');
                    setTimeout(() => {
                        cancelPopup();
                    }, 2000);
                } else {
                    setError(apidata[0].statusMessage || 'Error creating group numbers');
                    setSuccessMessage('');
                }
            } else {
                setError('Error creating group numbers');
                setSuccessMessage('');
            }
        } catch (error) {
            setError('Error creating group numbers');
            setSuccessMessage('');
        } finally {
            setBtnLoader(false);
        }
    };

    const cancelPopup = () => {
        closeCreateGroupPopup(wholesalerId, whName, activeWhPagin, activePagin, whRule, whSearch);
        openManageGroupPopup(wholesalerId, whName, activeWhPagin, activePagin, whRule, whSearch);
    };

    return (
        <Dialog open={true} onOpenChange={cancelPopup}>
            <DialogContent className="max-w-xl">
                <DialogHeader>
                    <DialogTitle>{titleText || 'Create Group Numbers'}</DialogTitle>
                </DialogHeader>

                {isMobile ? (
                    <MobileRestrictionMessage />
                ) : loading ? (
                    <div className="flex justify-center items-center py-8">
                        <Loader2 className="h-8 w-8 animate-spin text-primary" />
                    </div>
                ) : (
                    <form onSubmit={handleValidate} className="space-y-4">
                        <div className="space-y-2">
                            <Label>Select Prefix</Label>
                            <Select value={useOther ? 'Other' : selectedPrefix} onValueChange={handlePrefixChange}>
                                <SelectTrigger>
                                    <SelectValue placeholder="Select a prefix" />
                                </SelectTrigger>
                                <SelectContent className="max-h-[200px] overflow-y-auto">
                                    {prefixData && prefixData.length > 0 && prefixData[0].prefix.map((option, index) => (
                                        <SelectItem key={index} value={option}>
                                            {option}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>

                        <div className="text-center text-sm text-muted-foreground">OR</div>

                        <div className="space-y-2">
                            <Label>Create New Prefix</Label>
                            <Input
                                type="text"
                                value={otherPrependPrefix}
                                onChange={handleOtherPrefixChange}
                                disabled={!useOther}
                                placeholder="Enter new prefix"
                                maxLength={6}
                            />
                            {prefixError && (
                                <p className="text-sm text-destructive">{prefixError}</p>
                            )}
                        </div>

                        <div className="space-y-2">
                            <Label>Available Group Number from</Label>
                            <div className="text-lg font-semibold text-foreground">
                                {availableGroup}
                            </div>
                        </div>

                        <div className="space-y-2">
                            <Label>Number of Group Numbers</Label>
                            <Input
                                type="number"
                                value={numberOfGroups}
                                onChange={(e) => {
                                    setNumberOfGroups(e.target.value);
                                    setAvailableGroupError('');
                                }}
                                min="1"
                                max="1000"
                            />
                            {availableGroupError && (
                                <p className="text-sm text-destructive">{availableGroupError}</p>
                            )}
                        </div>

                        {successMessage && (
                            <div className="text-sm text-green-700 bg-green-50 p-3 rounded-md border border-green-200">
                                {successMessage}
                            </div>
                        )}

                        {error && (
                            <div className="text-sm text-destructive bg-destructive/10 p-3 rounded-md">
                                {error}
                            </div>
                        )}

                        <DialogFooter className="gap-2 sm:gap-0">
                            <Button 
                                type="submit"
                                disabled={btnLoader}
                                className="bg-primary hover:bg-primary/90"
                            >
                                {btnLoader && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                                Save & Create
                            </Button>
                            <Button 
                                type="button" 
                                variant="secondary" 
                                onClick={cancelPopup}
                                disabled={btnLoader}
                            >
                                Cancel
                            </Button>
                        </DialogFooter>
                    </form>
                )}
            </DialogContent>
        </Dialog>
    );
}

export default CreateGroup;
